import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState, AppDispatch, getClient, bearerAuth, validationErrors } from '@src/core/store';
import {
  Sale,
  Order,
  Voucher,
  SaleFormInput,
  SaleFormInputRequest,
  RescheduleScheduling,
} from '@src/core/buyerApiClient';
import { publicApiTokenSelector } from './auth';
import * as storage from '@src/common/storage';
import { getPaginationTotalCount } from '@src/common';
import { refreshOrderPageContainingItem } from './order';
import { organizeFormInputs } from '@src/common/formInputs';

const planneWidgetSaleReturnKey = 'planneWidgetSaleReturn';

export const PENDING = 'pending';
export const SENDING = 'sending';
export const COMPLETED = 'completed';
export type SaleFormInputsState = 'pending' | 'sending' | 'completed';

export interface SaleState {
  currentSale?: Sale;
  currentSaleVouchers?: Voucher[];
  saleRequiredFormInputs?: SaleFormInputs;
  saleItemRescheduleOptions: { [key: string]: RescheduleScheduling[] };
  saleItemRescheduleOngoing: boolean;
  loadingSaleItemRescheduleOptions: boolean;
}

const initialState: SaleState = {
  saleItemRescheduleOptions: {},
  saleItemRescheduleOngoing: false,
  loadingSaleItemRescheduleOptions: false,
};

interface SaleFormInputs {
  inputs: Array<SaleFormInput[]>;
  state: SaleFormInputsState;
  errors: RequiredFormInputsErrors;
  inputsToSend?: FinalSaleRequiredFormInput[];
}

export interface RequiredFormInputsErrors {
  [key: string]: string[];
}

export interface FinalSaleRequiredFormInput {
  hash: string;
  value: string;
  inputType: string;
}

export const saleSlice = createSlice({
  name: 'sale',
  initialState,
  reducers: {
    setConfirmedSale: {
      reducer(state: SaleState, action: PayloadAction<Sale, string, { vouchers: Voucher[] }>) {
        state.currentSale = action.payload;
        state.currentSaleVouchers = action.meta.vouchers;
      },
      prepare(payload: Sale, vouchers: Voucher[]) {
        return { payload, meta: { vouchers } };
      },
    },
    setProcessedSale: {
      reducer(state: SaleState, action: PayloadAction<Sale, string, { vouchers: Voucher[] }>) {
        state.currentSale = action.payload;
        state.currentSaleVouchers = action.meta.vouchers;
      },
      prepare(payload: Sale, vouchers: Voucher[]) {
        return { payload, meta: { vouchers } };
      },
    },
    setCurrentSale: {
      reducer(state: SaleState, action: PayloadAction<Sale, string, { vouchers: Voucher[] }>) {
        state.currentSale = action.payload;
        state.currentSaleVouchers = action.meta.vouchers;
      },
      prepare(payload: Sale, vouchers: Voucher[]) {
        return { payload, meta: { vouchers } };
      },
    },
    setCurrentReschedule: {
      reducer(state: SaleState, action: PayloadAction<RescheduleScheduling[], string, { id: string }>) {
        state.saleItemRescheduleOptions[action.meta.id] = action.payload;
      },
      prepare(payload: RescheduleScheduling[], id: string) {
        return { payload, meta: { id } };
      },
    },
    setLoadingSaleItemRescheduleOptions: (state: SaleState, action: PayloadAction<boolean, string>) => {
      state.loadingSaleItemRescheduleOptions = action.payload;
    },
    setSaleItemRescheduleOngoing: (state: SaleState, action: PayloadAction<boolean, string>) => {
      state.saleItemRescheduleOngoing = action.payload;
    },
    setSaleRequiredFormInputs: (state: SaleState, action: PayloadAction<Array<SaleFormInput[]>>) => {
      state.saleRequiredFormInputs = {
        inputs: action.payload,
        state: PENDING,
        errors: {},
      };
    },
    setRequiredFormInputsErrors: (state: SaleState, action: PayloadAction<RequiredFormInputsErrors>) => {
      if (state.saleRequiredFormInputs) {
        state.saleRequiredFormInputs.errors = action.payload;
      }
    },
    setSaleRequiredFormInputsState: {
      reducer(state: SaleState, action: PayloadAction<SaleFormInputsState, string, { sale: Sale }>) {
        if (state.saleRequiredFormInputs) {
          state.saleRequiredFormInputs.state = action.payload;
        }
      },
      prepare(payload: SaleFormInputsState, sale: Sale) {
        return { payload, meta: { sale } };
      },
    },
    triggerExternalAuthError: () => {},
    triggerExternalAuthStatus: (_: SaleState, __: PayloadAction<string, string>) => {},
    resetSale: (state: SaleState) => {
      state.currentSale = undefined;
      state.currentSaleVouchers = undefined;
    },
  },
});

export default saleSlice.reducer;

export const {
  setConfirmedSale,
  setCurrentSale,
  setProcessedSale,
  setCurrentReschedule,
  setLoadingSaleItemRescheduleOptions,
  setSaleItemRescheduleOngoing,
  setSaleRequiredFormInputs,
  setRequiredFormInputsErrors,
  setSaleRequiredFormInputsState,
  triggerExternalAuthError,
  triggerExternalAuthStatus,
  resetSale,
} = saleSlice.actions;

export const loadReturnSale = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const publicApiKey = publicApiTokenSelector(getState());
  const id = storage.getValue(publicApiKey!, planneWidgetSaleReturnKey);

  if (id == null) {
    return;
  }

  storage.removeValue(publicApiKey!, planneWidgetSaleReturnKey);
  loadSaleAsCurrent(dispatch, getState, id, setCurrentSale);
};

type SaleSetter = typeof setCurrentSale | typeof setConfirmedSale | typeof setProcessedSale;

export const loadSaleAsCurrent = async (
  dispatch: AppDispatch,
  getState: () => RootState,
  id: string,
  setter: SaleSetter,
) => {
  const state = getState();
  const {
    auth: { buyerToken },
    sale: { currentSale },
  } = state;

  if (buyerToken == null) {
    return;
  }

  const { data: sale } = await getClient(state).sales.getSale(id, bearerAuth(buyerToken));
  if (!currentSale || currentSale.id === sale.id) {
    const vouchers = await retrieveAllVouchersAsync(getState, sale);
    dispatch(setter(sale, vouchers));
  }
};

export const retrieveAllVouchersAsync = async (getState: () => RootState, sale: Sale) => {
  const state = getState();
  const {
    auth: { buyerToken },
  } = state;
  const client = getClient(state);

  const vouchers: Voucher[] = [];
  const pageSize = 100;

  for (let offset = 0, total = pageSize; offset < total; offset += pageSize) {
    const resp = await client.vouchers.getSaleVouchers(sale.id, offset, pageSize, bearerAuth(buyerToken!));

    vouchers.push(...resp.data);
    total = getPaginationTotalCount(resp);
  }

  return vouchers;
};

export const loadRescheduleAvailableDates =
  (saleItemId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      auth: { buyerToken },
    } = state;
    if (buyerToken == null) {
      return;
    }

    const schedulings: RescheduleScheduling[] = [];
    const pageSize = 100;
    const client = getClient(state);

    try {
      dispatch(setLoadingSaleItemRescheduleOptions(true));

      for (let offset = 0, total = pageSize; offset < total; offset += pageSize) {
        const resp = await client.schedulings.getAvailableSaleItemSchedulings(
          saleItemId,
          offset,
          pageSize,
          bearerAuth(buyerToken),
        );

        schedulings.push(...resp.data);
        total = getPaginationTotalCount(resp);
      }

      dispatch(setCurrentReschedule(schedulings, saleItemId));
    } finally {
      dispatch(setLoadingSaleItemRescheduleOptions(false));
    }
  };

export const processReschedule =
  (saleItemId: string, date: RescheduleScheduling, scheduleTimeMinutes: number | null) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      auth: { buyerToken },
      sale,
    } = state;

    if (buyerToken == null) {
      return;
    }

    dispatch(setSaleItemRescheduleOngoing(true));

    try {
      await getClient(state).saleItems.rescheduleSaleItem(
        saleItemId,
        { scheduleDate: date.date, scheduleTimeMinutes },
        bearerAuth(buyerToken),
      );

      if (sale.currentSale?.id) {
        loadSaleAsCurrent(dispatch, getState, sale.currentSale?.id, setCurrentSale);
      }
    } catch (err) {
      alert('Ops, houve um erro com seu agendamento!');

      throw err;
    } finally {
      dispatch(setSaleItemRescheduleOngoing(false));
    }

    dispatch(refreshOrderPageContainingItem(saleItemId));
  };

export const saleRequiredFormInputsSelector = (state: RootState) =>
  state.sale.saleRequiredFormInputs
    ? {
        ...state.sale.saleRequiredFormInputs,
        qtd: state.sale.saleRequiredFormInputs.inputs.reduce((acc, value) => acc + value.length, 0),
      }
    : null;
export const hasPendingSaleRequiredFormInputsSelector = (state: RootState): boolean => {
  const saleRequiredFormInputs = saleRequiredFormInputsSelector(state);

  return (
    saleRequiredFormInputs != null &&
    saleRequiredFormInputs.qtd > 0 &&
    saleRequiredFormInputs.state != COMPLETED &&
    saleRequiredFormInputs.inputs.some((inputs) => inputs.some((formInput) => formInput.value == null))
  );
};

export const currentSaleSelector = (state: RootState) => state.sale.currentSale;
export const currentSaleHasVouchersSelector = (state: RootState) =>
  state.sale.currentSaleVouchers ? state.sale.currentSaleVouchers.length > 0 : false;

export const currentRescheduleSelector = (saleItemId: string) => (state: RootState) => {
  return (state.sale.saleItemRescheduleOptions[saleItemId] ?? []).reduce<{
    [key: string]: RescheduleScheduling[];
  }>((carry, scheduling) => {
    if (scheduling.scheduleAvailabilities.every((ast) => !ast.available)) {
      return carry;
    }

    return {
      ...carry,
      [scheduling.date]: [...(carry[scheduling.date] ?? []), scheduling],
    };
  }, {});
};
export const currentOrderRescheduleSelector = (orderId: string | null) => (state: RootState) => {
  return Object.values(state.order.paginatedOrders.pages).reduce(
    (curr: Order | undefined, orders) => orders.find((o) => o.id === orderId) ?? curr,
    undefined,
  );
};
export const saleItemRescheduleOngoingSelector = (state: RootState) => state.sale.saleItemRescheduleOngoing;

export const fetchSaleRequiredFormInputs = (sale: Sale) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    auth: { buyerToken },
  } = state;

  if (buyerToken == null) {
    return;
  }

  const formInputs: SaleFormInput[] = [];
  const pageSize = 100;
  const client = getClient(state);

  for (let offset = 0, total = pageSize; offset < total; offset += pageSize) {
    const resp = await client.formInputs.getSaleRequiredFormInputs(sale.id, offset, pageSize, bearerAuth(buyerToken));

    formInputs.push(...resp.data);
    total = getPaginationTotalCount(resp);
  }

  dispatch(setSaleRequiredFormInputs(organizeFormInputs(formInputs)));
  loadSaleAsCurrent(dispatch, getState, sale.id, setCurrentSale);
};

export const sendSaleRequiredFormInput =
  (formInputs: SaleFormInputRequest[], sale: Sale) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      auth: { buyerToken },
      sale: { currentSale },
    } = state;

    if (buyerToken == null) {
      return;
    }

    dispatch(setSaleRequiredFormInputsState(SENDING, sale));

    try {
      await getClient(state).formInputs.setSaleRequiredFormInputs(
        sale.id,
        { inputs: formInputs },
        bearerAuth(buyerToken),
      );
    } catch (err) {
      dispatch(setSaleRequiredFormInputsState(PENDING, sale));

      const errors = validationErrors(err);
      if (errors) {
        dispatch(setRequiredFormInputsErrors(errors));
      } else {
        throw err;
      }

      return;
    }

    dispatch(setSaleRequiredFormInputsState(COMPLETED, sale));
    loadSaleAsCurrent(dispatch, getState, sale.id, setCurrentSale);
  };

export const loadingSaleItemRescheduleOptionsSelector = (state: RootState) =>
  state.sale.loadingSaleItemRescheduleOptions;
